import type { StationRequestRecord } from "../../../services/FormService";

/** Stored on Register Form `partnerInfo.businessType` after station-portal legal step; also used in admin edit dropdown. */
export const STATION_PORTAL_LEGAL_BUSINESS_TYPE_COMPANY = "Company";
export const STATION_PORTAL_LEGAL_BUSINESS_TYPE_INDIVIDUAL = "Individual / Sole Proprietor";

/** Maps raw `stationPortalLegalVerification.businessType` (`company` | `individual`) to admin-facing labels. */
export function partnerInfoBusinessTypeFromLegalRaw(raw?: string | null): string {
	const bt = String(raw || "").toLowerCase();
	if (bt === "company") return STATION_PORTAL_LEGAL_BUSINESS_TYPE_COMPANY;
	if (bt === "individual") return STATION_PORTAL_LEGAL_BUSINESS_TYPE_INDIVIDUAL;
	return "";
}

/**
 * Business type shown in Station Requests Profile / edit: prefer `partnerInfo`, then legal-verification raw on the form.
 */
export function resolveStationRequestBusinessTypeDisplay(
	request?: StationRequestRecord | null,
): string {
	const rawDirect = String(
		request?.partnerInfo?.businessType ||
			request?.partnerInfo?.luggageType ||
			// Đường lùi: form đăng ký cũ ghi businessType vào contractInfo — đọc nốt để không
			// hiện N/A với hồ sơ đã nộp trước khi form gửi vào partnerInfo (yêu cầu Quân 02/09).
			request?.contractInfo?.businessType ||
			"",
	).trim();
	// Hồ sơ cũ lưu chữ thường "company"/"individual" — đổi sang NHÃN chuẩn để khớp dropdown
	// và không hiện chữ thường lạ (yêu cầu Quân 02/09).
	const direct = partnerInfoBusinessTypeFromLegalRaw(rawDirect) || rawDirect;
	if (direct) return direct;
	return partnerInfoBusinessTypeFromLegalRaw(
		request?.extenalInfo?.stationPortalLegalVerification?.businessType,
	);
}

export const STATION_REQUEST_EDIT_BUSINESS_TYPE_OPTIONS = [
	"Hotel",
	"Hostel",
	"Others",
	STATION_PORTAL_LEGAL_BUSINESS_TYPE_COMPANY,
	STATION_PORTAL_LEGAL_BUSINESS_TYPE_INDIVIDUAL,
] as const;

type PortalLegalRaw = {
	completed?: boolean;
	completedAt?: string;
	summaryEn?: string;
	businessType?: string;
	businessName?: string;
	representativeName?: string;
	representativePhone?: string;
};

function readPortalLegal(request?: StationRequestRecord | null): PortalLegalRaw | null {
	const raw = request?.extenalInfo?.stationPortalLegalVerification;
	if (!raw || typeof raw !== "object") return null;
	return raw as PortalLegalRaw;
}

function isLegalCompleteRaw(raw: PortalLegalRaw | null): boolean {
	return Boolean(raw?.completed === true);
}

/** True when partner finished Welcome / Legal & ID and tapped Complete Registration in station portal. */
export function isStationPortalLegalVerificationCompleted(
	request?: StationRequestRecord | null,
): boolean {
	return isLegalCompleteRaw(readPortalLegal(request));
}

/** English copy for admin Station Requests — station portal onboarding legal form. */
export function getStationPortalLegalVerificationPanelEn(request?: StationRequestRecord | null): {
	completed: boolean;
	statusLine: string;
	rows: { label: string; value: string }[];
} {
	const raw = readPortalLegal(request);
	const completed = isLegalCompleteRaw(raw);

	if (!completed) {
		return {
			completed: false,
			statusLine: "Not submitted via station portal yet.",
			rows: [],
		};
	}

	const summary =
		typeof raw.summaryEn === "string" && raw.summaryEn.trim()
			? raw.summaryEn.trim()
			: "Legal & identification fully completed (station portal)";

	const when =
		typeof raw.completedAt === "string" && raw.completedAt ? new Date(raw.completedAt) : null;
	const whenStr =
		when && !Number.isNaN(when.getTime())
			? when.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })
			: "";

	const statusLine = whenStr ? `${summary} · ${whenStr}` : summary;

	const entity =
		partnerInfoBusinessTypeFromLegalRaw(raw.businessType) ||
		String(raw.businessType || "").trim() ||
		"-";

	const rows = [
		{ label: "Entity type", value: entity },
		{ label: "Legal business name", value: String(raw.businessName || "").trim() || "-" },
		{ label: "Representative name", value: String(raw.representativeName || "").trim() || "-" },
		{ label: "Representative phone", value: String(raw.representativePhone || "").trim() || "-" },
	];

	return { completed: true, statusLine, rows };
}
